import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";

interface Product {
  id: string;
  name: string;
  brand: string;
  os: string;
  ram: string;
  rom: string;
  color: string;
  price: string;
  image: string;
}

const ProductsPage: React.FC = () => {
  const { brand } = useParams<{ brand: string }>();
  const formattedBrand = brand
    ? brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
    : "";

  const [products, setProducts] = useState<Product[]>([]);
  const [filters, setFilters] = useState({
    brand: formattedBrand ?? "",
    os: "",
    ram: "All RAM",
    rom: "All ROM",
    color: "",
    priceRange: [0, 1000],
    sort: "asc",
  });
  const [options, setOptions] = useState<{
    brands: string[];
    os: string[];
    colors: string[];
    ram: string[];
    rom: string[];
  }>({ brands: [], os: [], colors: [], ram: [], rom: [] });
  const [isMobile, setIsMobile] = useState(false);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(1000);

  useEffect(() => {
    const fetchOptions = async () => {
      const brandsSet = new Set<string>();
      const osSet = new Set<string>();
      const colorsSet = new Set<string>();
      const ramSet = new Set<string>();
      const romSet = new Set<string>();

      const querySnapshot = await getDocs(collection(db, "proudcts"));
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        brandsSet.add(data.brand);
        osSet.add(data.os);
        colorsSet.add(data.color);
        ramSet.add(data.ram);
        romSet.add(data.rom);
      });

      setOptions({
        brands: Array.from(brandsSet),
        os: Array.from(osSet),
        colors: Array.from(colorsSet),
        ram: Array.from(ramSet).sort(),
        rom: Array.from(romSet).sort(),
      });
    };

    const fetchPriceRange = async () => {
      const q = query(
        collection(db, "proudcts"),
        orderBy("price", "asc"),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const lowestPrice = querySnapshot.docs[0].data();
        setMinPrice(Number(lowestPrice.price) || 0);
      }

      const qMax = query(
        collection(db, "proudcts"),
        orderBy("price", "desc"),
        limit(1)
      );
      const maxQuerySnapshot = await getDocs(qMax);
      if (!maxQuerySnapshot.empty) {
        const highestPrice = maxQuerySnapshot.docs[0].data();
        setMaxPrice(Number(highestPrice.price) || 1000);
        setFilters((prev) => ({
          ...prev,
          priceRange: [minPrice, Number(highestPrice.price) || 1000],
        }));
      }
    };

    fetchOptions();
    fetchPriceRange();
  }, [minPrice]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchFilteredProducts = async () => {
    const productsRef = collection(db, "proudcts");
    let q = query(productsRef);

    if (filters.brand) {
      q = query(q, where("brand", "==", filters.brand));
    }
    if (filters.os) {
      q = query(q, where("os", "==", filters.os));
    }
    if (filters.ram !== "All RAM") {
      q = query(q, where("ram", "==", filters.ram));
    }
    if (filters.rom !== "All ROM") {
      q = query(q, where("rom", "==", filters.rom));
    }
    if (filters.color) {
      q = query(q, where("color", "==", filters.color));
    }

    const minPrice = Number(filters.priceRange[0]);
    const maxPrice = Number(filters.priceRange[1]);

    if (!isNaN(minPrice) && !isNaN(maxPrice)) {
      q = query(
        q,
        where("price", ">=", String(minPrice)),
        where("price", "<=", String(maxPrice))
      );
    }

    if (filters.sort === "asc") {
      q = query(q, orderBy("price", "asc"));
    } else if (filters.sort === "desc") {
      q = query(q, orderBy("price", "desc"));
    }

    try {
      const querySnapshot = await getDocs(q);
      const filteredProducts = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Product[];

      setProducts(filteredProducts);
    } catch (error) {
      console.error("Error fetching filtered products:", error);
    }
  };

  useEffect(() => {
    fetchFilteredProducts();
  }, [filters]);

  const updateFilter = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-white text-black my-10 px-5 lg:px-36">
      <div className="bg-blue-500 text-white text-center py-4 mb-6 rounded-md shadow-md">
        <h2 className="text-xl font-bold">
          Big Deal! 50% sale to Samsung A-series smartphones!
        </h2>
        <p className="text-sm">
          Hurry up to buy goods at favorable prices! Only 1st of january
        </p>
      </div>

      <div className={`flex ${isMobile ? "flex-col" : ""} mt-4`}>
        {isMobile ? (
          <div className="w-full mb-4">
            <Filters
              filters={filters}
              updateFilter={updateFilter}
              minPrice={minPrice}
              maxPrice={maxPrice}
              options={options}
              fetchFilteredProducts={fetchFilteredProducts}
            />
          </div>
        ) : (
          <div className="w-1/4">
            <Filters
              filters={filters}
              updateFilter={updateFilter}
              minPrice={minPrice}
              maxPrice={maxPrice}
              options={options}
              fetchFilteredProducts={fetchFilteredProducts}
            />
          </div>
        )}

        <div className={`flex-grow ${isMobile ? "" : "ml-4"}`}>
          <ProductsList products={products} />
        </div>
      </div>
    </div>
  );
};

const Filters: React.FC<{
  filters: any;
  updateFilter: (key: string, value: any) => void;
  minPrice: number;
  maxPrice: number;
  options: any;
  fetchFilteredProducts: () => void;
}> = ({
  filters,
  updateFilter,
  minPrice,
  maxPrice,
  options,
  fetchFilteredProducts,
}) => {
  return (
    <div className="bg-white p-4 rounded shadow">
      <h3 className="text-lg font-bold mb-4">Filters</h3>
      <select
        value={filters.brand}
        onChange={(e) => updateFilter("brand", e.target.value)}
        className="w-full p-2 mb-4 border border-gray-300 rounded bg-white text-black"
      >
        <option value="">All Brands</option>
        {options.brands.map((brand: string) => (
          <option key={brand} value={brand}>
            {brand}
          </option>
        ))}
      </select>

      <select
        value={filters.os}
        onChange={(e) => updateFilter("os", e.target.value)}
        className="w-full p-2 mb-4 border border-gray-300 rounded bg-white text-black"
      >
        <option value="">All OS</option>
        {options.os.map((os: string) => (
          <option key={os} value={os}>
            {os}
          </option>
        ))}
      </select>

      <select
        value={filters.ram}
        onChange={(e) => updateFilter("ram", e.target.value)}
        className="w-full p-2 mb-4 border border-gray-300 rounded bg-white text-black"
      >
        <option value="All RAM">All RAM</option>
        {options.ram.map((ram: string) => (
          <option key={ram} value={ram}>
            {ram} GB
          </option>
        ))}
      </select>

      <select
        value={filters.rom}
        onChange={(e) => updateFilter("rom", e.target.value)}
        className="w-full p-2 mb-4 border border-gray-300 rounded bg-white text-black"
      >
        <option value="All ROM">All ROM</option>
        {options.rom.map((rom: string) => (
          <option key={rom} value={rom}>
            {rom} GB
          </option>
        ))}
      </select>

      <select
        value={filters.color}
        onChange={(e) => updateFilter("color", e.target.value)}
        className="w-full p-2 mb-4 border border-gray-300 rounded bg-white text-black"
      >
        <option value="">All Colors</option>
        {options.colors.map((color: string) => (
          <option key={color} value={color}>
            {color}
          </option>
        ))}
      </select>

      <div className="mb-4">
        <label className="block text-black mb-2">Price</label>
        <input
          type="range"
          min={minPrice}
          max={maxPrice}
          value={filters.priceRange[1]}
          onChange={(e) =>
            updateFilter("priceRange", [
              filters.priceRange[0],
              Number(e.target.value),
            ])
          }
          className="w-full"
        />
        <div className="flex justify-between">
          <span>${filters.priceRange[0]}</span>
          <span>${filters.priceRange[1]}</span>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-black mb-2">Sort by Price</label>
        <select
          value={filters.sort}
          onChange={(e) => updateFilter("sort", e.target.value)}
          className="w-full p-2 border border-gray-300 rounded bg-white text-black"
        >
          <option value="default">Default</option>
          <option value="asc">Price: Low to High</option>
          <option value="desc">Price: High to Low</option>
        </select>
      </div>

      <button
        onClick={fetchFilteredProducts}
        className="w-full py-2 bg-blue-600 text-white rounded"
      >
        Apply Filters
      </button>
    </div>
  );
};

const ProductsList: React.FC<{ products: Product[] }> = ({ products }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {products.map((product) => (
        <div key={product.id} className="bg-white p-4 rounded shadow">
          <Link to={`/products/${product.id}`}>
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-64 object-cover mb-4"
            />
          </Link>
          <Link to={`/products/${product.id}`}>
            <h4 className="font-bold mb-2">{product.name}</h4>
          </Link>
          <p>Ram: {product.ram}</p>
          <p>Rom: {product.rom}</p>
          <p>Color: {product.color}</p>
          <p>Price: ${product.price}</p>
          <Link to={`/products/${product.id}`}>
            <div className="px-4 py-2 mt-4 bg-red-700 text-white rounded-lg text-center">
              Overview
            </div>
          </Link>
        </div>
      ))}
    </div>
  );
};

export default ProductsPage;
